import { useMutation } from '@apollo/client'
import { createContext, useContext, useMemo, useState } from 'react'

import { NotificationDialog } from '..'

const ConfirmContext = createContext(null)

function ConfirmDialog({ confirm, reset }) {
  const {
    title = '',
    message = '',
    success = '',
    error = '',
    mutation,
    variables,
    refetchQueries = [],
    onSuccess = null,
    onClose = null,
    onCancel = null,
  } = confirm
  const open = !!mutation
  const [mutate, { data, error: mutationError }] = useMutation(mutation, {
    refetchQueries,
  })

  const handleSuccessClose = () => {
    reset()
    if (onSuccess) {
      onSuccess()
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    reset()
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    reset()
  }

  // Show success notification
  if (data) {
    return (
      <NotificationDialog
        open={open}
        onClose={handleSuccessClose}
        type="success"
        title="Success!"
        message={success}
        actions={[{ label: 'Done', onClick: handleSuccessClose }]}
      />
    )
  }

  // Show error dialog
  if (mutationError) {
    return (
      <NotificationDialog
        open={open}
        onClose={handleClose}
        type="error"
        title="Uh Oh!"
        message={error || mutationError.message}
        actions={[{ label: 'Back', onClick: reset }]}
      />
    )
  }

  // Show confirmation dialog
  return (
    <NotificationDialog
      open={open}
      onClose={handleCancel}
      type="alert"
      title={title}
      message={typeof message === 'string' ? message : <div>{message}</div>}
      actions={[
        { label: 'Back', onClick: handleCancel },
        { label: 'Confirm', onClick: () => mutate({ variables }) },
      ]}
    />
  )
}

export function ConfirmProvider({ children }) {
  const initialState = {
    title: '',
    message: '',
    success: '',
    error: '',
    mutation: null,
    variables: null,
    onClose: null,
    onCancel: null,
  }
  const [confirm, setConfirm] = useState(initialState)
  const reset = () => setConfirm(initialState)
  const value = useMemo(() => [confirm, { setConfirm }], [confirm, setConfirm])
  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {confirm?.mutation ? <ConfirmDialog confirm={confirm} reset={reset} /> : null}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  return useContext(ConfirmContext)
}
